import reactUpdate from 'immutability-helper';
import React from 'react';

import ChooseAaSequenceAlignmentPane from 'analyses/aa-sequence-alignment/ChooseAaSequenceAlignmentPane';
import ConfirmAaSequenceAlignmentPane from 'analyses/aa-sequence-alignment/ConfirmAaSequenceAlignmentPane';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import AaSequenceAlignmentActions from 'molbio/aa-sequences/alignments/AaSequenceAlignmentActions';
import notifications from 'notifications';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

import {AaSequenceSpecifier} from './util/AaSequenceAlignmentConstants';

const AA_SEQUENCE_ALIGNMENT_MODAL_KEY = 'ProteinAlignmentModal';

const CHOOSE_PANE = 'CHOOSE_PANE'; // First step
const CONFIRM_PANE = 'CONFIRM_PANE';

interface Props {
  initialAaSequenceSpecifiers: Array<AaSequenceSpecifier>;
  initialTemplateAaSequenceIndex?: number;
}

interface State {
  proteinSpecifiers: Array<AaSequenceSpecifier>;
  templateProteinIndex: Optional<number>;
  proteinName: string | null;
  folderId: Optional<string>;
  currentPane: string;
}

class AaSequenceAlignmentModal extends React.Component<Props, State> {
  static open({initialAaSequenceSpecifiers}: Props): void {
    LegacyModalContainer.open(
      AA_SEQUENCE_ALIGNMENT_MODAL_KEY,
      <AaSequenceAlignmentModal initialAaSequenceSpecifiers={initialAaSequenceSpecifiers} />
    );
  }

  static openWithTemplateSpecifier(templateAaSequenceSpecifier: AaSequenceSpecifier): void {
    LegacyModalContainer.open(
      AA_SEQUENCE_ALIGNMENT_MODAL_KEY,
      <AaSequenceAlignmentModal
        initialTemplateAaSequenceIndex={0}
        initialAaSequenceSpecifiers={[templateAaSequenceSpecifier]}
      />
    );
  }

  override state: State = {
    proteinSpecifiers: this.props.initialAaSequenceSpecifiers,
    templateProteinIndex: this.props.initialTemplateAaSequenceIndex,
    proteinName: null,
    folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
    currentPane: CHOOSE_PANE,
  };

  _shouldCreateNewAaSequence = (): boolean => this.state.templateProteinIndex == null;

  _shouldEnableCreateAlignment = (): boolean => {
    if (this._shouldCreateNewAaSequence()) {
      return this.state.proteinName != null && this.state.folderId != null;
    } else {
      return true;
    }
  };

  _handleRequestClose = (): void => {
    LegacyModalContainer.close(AA_SEQUENCE_ALIGNMENT_MODAL_KEY);
  };

  _handleAddAaSequenceSpecifier = (aaSequenceSpecifier: AaSequenceSpecifier): void => {
    this.setState({
      proteinSpecifiers: reactUpdate(this.state.proteinSpecifiers, {$push: [aaSequenceSpecifier]}),
    });
  };

  _handleRemoveAaSequenceAt = (index: number): void => {
    this.setState({
      proteinSpecifiers: reactUpdate(this.state.proteinSpecifiers, {$splice: [[index, 1]]}),
    });
    if (index < this.state.templateProteinIndex!) {
      this.setState({templateProteinIndex: this.state.templateProteinIndex! - 1});
    } else if (index === this.state.templateProteinIndex) {
      this.setState({templateProteinIndex: null});
    }
  };

  _handleSetTemplateAaSequenceAt = (maybeIndex: number | null): void => {
    if (maybeIndex != null && !this.state.proteinSpecifiers[maybeIndex].canBeTemplate) {
      notifications.warn('Cannot set read-only AA sequence as template.', {
        id: 'ProteinAlignmentModal: Cannot set read-only AA sequence as template.',
      });
    } else {
      this.setState({
        templateProteinIndex: maybeIndex,
      });
    }
  };

  _handleSetAaSequenceName = (proteinName: string): void => {
    this.setState({proteinName});
  };

  _handleSetFolderId = (folderId: string): void => {
    this.setState({folderId});
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
  };

  _handleCreateAlignment = (): void => {
    AaSequenceAlignmentActions.create({
      proteinName: this.state.proteinName!,
      folderId: this._shouldCreateNewAaSequence() ? this.state.folderId! : undefined,
      templateProteinIndex: this.state.templateProteinIndex,
      proteinSpecifiers: this.state.proteinSpecifiers,
    });
    this._handleRequestClose();
  };

  _getButtons: () => Array<ModalButton> = () => {
    if (this.state.currentPane === CHOOSE_PANE) {
      return [
        {
          text: 'Next',
          style: 'primary',
          disabled: this.state.proteinSpecifiers.length <= 1,
          onClick: () => {
            this.setState({currentPane: CONFIRM_PANE});
          },
          key: 'next',
          dataTestAttrs: {
            component: 'ProteinAlignmentModal',
            element: 'footerButton',
            key: ModalButtonKey.NEXT,
          },
        },
      ];
    } else {
      return [
        {
          text: 'Back',
          pullLeft: true,
          onClick: () => {
            this.setState({currentPane: CHOOSE_PANE});
          },
          key: 'back',
        },
        {
          text: 'Create Alignment',
          style: 'primary',
          disabled: !this._shouldEnableCreateAlignment(),
          onClick: this._handleCreateAlignment,
          key: 'create',
          dataTestAttrs: {
            component: 'ProteinAlignmentModal',
            element: 'footerButton',
            key: ModalButtonKey.CREATE,
          },
        },
      ];
    }
  };

  _renderPane = (): JSX.Element => {
    if (this.state.currentPane === CHOOSE_PANE) {
      return (
        <ChooseAaSequenceAlignmentPane
          proteinSpecifiers={this.state.proteinSpecifiers}
          templateProteinIndex={this.state.templateProteinIndex}
          onAddAaSequenceSpecifier={this._handleAddAaSequenceSpecifier}
          onRemoveAaSequenceAt={this._handleRemoveAaSequenceAt}
          onSetTemplateAaSequenceAt={this._handleSetTemplateAaSequenceAt}
        />
      );
    } else {
      return (
        <ConfirmAaSequenceAlignmentPane
          shouldCreateNewAaSequence={this._shouldCreateNewAaSequence()}
          proteinName={this.state.proteinName}
          onSetAaSequenceName={this._handleSetAaSequenceName}
          folderId={this.state.folderId}
          onSetFolderId={this._handleSetFolderId}
        />
      );
    }
  };

  override render(): JSX.Element {
    return (
      <Modal
        title="Create AA sequence alignment"
        onRequestClose={this._handleRequestClose}
        buttons={this._getButtons()}
      >
        {this._renderPane()}
      </Modal>
    );
  }
}

export default AaSequenceAlignmentModal;
